import type { ReportConfig } from "../types/reports";
import { getAuthHeaders, handleAuthError } from "./auth-api";
import { formatApiError } from "./error-message";

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setConfigBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

interface ApiResponse<T> {
  message: string;
  data: T;
}

async function fetchConfig<T>(endpoint: string): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  const result: ApiResponse<T> = await response.json();
  return result.data;
}

async function putConfig<T>(endpoint: string, body: unknown): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json();
}

// --- Access Control (some need shopId) ---

export async function fetchProfiles<T = unknown[]>(shopId: string): Promise<T> {
  return fetchConfig<T>(
    `/access-control/profiles/${encodeURIComponent(shopId)}`,
  );
}

export async function fetchAccessControls<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/access-control/${encodeURIComponent(shopId)}`);
}

export async function fetchFieldControls<T = unknown[]>(): Promise<T> {
  return fetchConfig<T>("/access-control/fields");
}

export async function fetchMasterControls<T = unknown[]>(): Promise<T> {
  return fetchConfig<T>("/access-control/masters");
}

export async function fetchShortcuts<T = unknown[]>(): Promise<T> {
  return fetchConfig<T>("/access-control/shortcuts/all");
}

export async function fetchStores<T = unknown[]>(shopId: string): Promise<T> {
  return fetchConfig<T>(`/access-control/stores/${encodeURIComponent(shopId)}`);
}

// --- Store Config (all need shopId) ---

export async function fetchStoreConfig<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/config`);
}

export async function fetchSalesConfig<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/sales-config`);
}

export async function fetchLoyaltyConfig<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/loyalty-config`);
}

export async function fetchPrintConfig<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/print-config`);
}

export async function updatePrintConfig(
  shopId: string,
  body: import("../types/control-management").UpdateThermalPrintPayload,
): Promise<{ message: string }> {
  return putConfig<{ message: string }>(
    `/stores/${encodeURIComponent(shopId)}/print-config`,
    body,
  );
}

export async function fetchColorMaster<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/colors`);
}

export async function fetchMtypeMaster<T = unknown[]>(
  shopId: string,
): Promise<T> {
  return fetchConfig<T>(`/stores/${encodeURIComponent(shopId)}/mtype-master`);
}

// --- Report catalog (ported wobpos ReportConfig_v2.json) ---

export async function fetchReportConfig(): Promise<ReportConfig> {
  return fetchConfig<ReportConfig>("/config/reports");
}
